import { Popover, PopoverContent, PopoverTrigger } from '@heroui/react';
import { type Editor } from '@tiptap/react';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';

// --- Hooks ---
import { useMenuNavigation } from '../../../hooks/use-menu-navigation';
import { useTiptapEditor } from '../../../hooks/use-tiptap-editor';
// --- Icons ---
import { BanIcon } from '../../tiptap-icons/ban-icon';
import { HighlighterIcon } from '../../tiptap-icons/highlighter-icon';
// --- Tiptap UI ---
import type { HighlightColor, UseColorHighlightConfig } from '../../tiptap-ui/color-highlight-button';
import {
  ColorHighlightButton,
  pickHighlightColorsByValue,
  useColorHighlight,
} from '../../tiptap-ui/color-highlight-button';
// --- UI Primitives ---
import type { ButtonProps } from '../../tiptap-ui-primitive/button';
import { Button, ButtonGroup } from '../../tiptap-ui-primitive/button';
import { CardItemGroup } from '../../tiptap-ui-primitive/card';
import { Separator } from '../../tiptap-ui-primitive/separator';

export interface ColorHighlightPopoverContentProps {
  /**
   * The Tiptap editor instance.
   */
  editor?: Editor | null;
  /**
   * Optional colors to use in the highlight popover.
   * If not provided, defaults to a predefined set of colors.
   */
  colors?: HighlightColor[];
}

export interface ColorHighlightPopoverProps
  extends Omit<ButtonProps, 'type'>, Pick<UseColorHighlightConfig, 'editor' | 'hideWhenUnavailable' | 'onApplied'> {
  /**
   * Optional colors to use in the highlight popover.
   * If not provided, defaults to a predefined set of colors.
   */
  colors?: HighlightColor[];
}

export const ColorHighlightPopoverButton = forwardRef<
  HTMLButtonElement,
  ButtonProps & Pick<UseColorHighlightConfig, 'editor'>
>(({ className, children, editor: providedEditor, ...props }, ref) => {
  const { editor } = useTiptapEditor(providedEditor);

  const [isVisible, setIsVisible] = useState(editor?.isEditable);

  useEffect(() => {
    if (!editor) return;

    const handleSelectionUpdate = () => {
      setIsVisible(editor.isEditable);
    };

    editor.on('transaction', handleSelectionUpdate);

    return () => {
      editor.off('transaction', handleSelectionUpdate);
    };
  }, [editor]);

  if (!isVisible) {
    return null;
  }

  return (
    <Button
      type="button"
      className={className}
      data-style="ghost"
      data-appearance="default"
      role="button"
      tabIndex={-1}
      aria-label="Highlight text"
      tooltip="Highlight"
      ref={ref}
      {...props}
    >
      {children ?? <HighlighterIcon className="tiptap-button-icon" />}
    </Button>
  );
});

ColorHighlightPopoverButton.displayName = 'ColorHighlightPopoverButton';

export function ColorHighlightPopoverContent({
  editor,
  colors = pickHighlightColorsByValue([
    'var(--tt-color-highlight-green)',
    'var(--tt-color-highlight-blue)',
    'var(--tt-color-highlight-red)',
    'var(--tt-color-highlight-purple)',
    'var(--tt-color-highlight-yellow)',
  ]),
}: ColorHighlightPopoverContentProps) {
  const { handleRemoveHighlight } = useColorHighlight({ editor });
  const containerRef = useRef<HTMLDivElement>(null);

  const menuItems = useMemo(() => [...colors, { label: 'Remove highlight', value: 'none' }], [colors]);

  const { selectedIndex } = useMenuNavigation({
    containerRef,
    items: menuItems,
    orientation: 'both',
    onSelect: (item) => {
      if (!containerRef.current) return false;
      const highlightedElement = containerRef.current.querySelector('[data-highlighted="true"]') as HTMLElement;
      if (highlightedElement) highlightedElement.click();
      if (item.value === 'none') handleRemoveHighlight();
      return true;
    },
    autoSelectFirstItem: false,
  });

  return (
    <CardItemGroup orientation="horizontal">
      <ButtonGroup orientation="horizontal">
        {colors.map((color, index) => (
          <ColorHighlightButton
            key={color.value}
            editor={editor}
            highlightColor={color.value}
            tooltip={color.label}
            aria-label={`${color.label} highlight color`}
            tabIndex={index === selectedIndex ? 0 : -1}
            data-highlighted={selectedIndex === index}
          />
        ))}
      </ButtonGroup>
      <Separator />
      <ButtonGroup orientation="horizontal">
        <Button
          onClick={handleRemoveHighlight}
          aria-label="Remove highlight"
          tooltip="Remove highlight"
          tabIndex={selectedIndex === colors.length ? 0 : -1}
          type="button"
          role="menuitem"
          data-style="ghost"
          data-highlighted={selectedIndex === colors.length}
        >
          <BanIcon className="tiptap-button-icon" />
        </Button>
      </ButtonGroup>
    </CardItemGroup>
  );
}

export function ColorHighlightPopover({
  editor: providedEditor,
  colors = pickHighlightColorsByValue([
    'var(--tt-color-highlight-green)',
    'var(--tt-color-highlight-blue)',
    'var(--tt-color-highlight-red)',
    'var(--tt-color-highlight-purple)',
    'var(--tt-color-highlight-yellow)',
  ]),
  hideWhenUnavailable = false,
  onApplied,
  ...props
}: ColorHighlightPopoverProps) {
  const { editor } = useTiptapEditor(providedEditor);
  const [isOpen, setIsOpen] = useState(false);
  const { isVisible, canColorHighlight, isActive, label, Icon } = useColorHighlight({
    editor,
    hideWhenUnavailable,
    onApplied,
  });

  if (!isVisible) return null;

  return (
    <Popover isOpen={isOpen} placement="bottom" onOpenChange={setIsOpen}>
      <PopoverTrigger>
        <ColorHighlightPopoverButton
          disabled={!canColorHighlight}
          data-active-state={isActive ? 'on' : 'off'}
          data-disabled={!canColorHighlight}
          aria-pressed={isActive}
          aria-label={label}
          tooltip={label}
          {...props}
        >
          <Icon className="tiptap-button-icon" />
        </ColorHighlightPopoverButton>
      </PopoverTrigger>
      <PopoverContent aria-label="Highlight colors">
        <ColorHighlightPopoverContent editor={editor} colors={colors} />
      </PopoverContent>
    </Popover>
  );
}

export default ColorHighlightPopover;
